import { useMemo, useState, type ReactNode } from "react";
import { Box, IconButton, Stack, Typography, Button } from "@mui/material";
import { CirclePlus, ClipboardList, LayoutDashboard, Menu } from "lucide-react";
import {
  AppShell,
  PageHeader,
  RavenIcon,
  SideNavigation,
  type SideNavItem,
} from "@ravenappdev/raven-ui";
import { alpha, useTheme } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTopBarActions, useTopBarTitle } from "../context/TopBarActionsContext";
import SwitchUserMenu from "./SwitchUserMenu";
import CountChip from "./CountChip";
import { getSuggestionStats, listSuggestions } from "../api/ess";
import { useCurrentUserProfile } from "../hooks/useUserProfile";
import { isEmployeeOnly } from "../hooks/useIsAdmin";
import { useDepartments } from "../hooks/useDepartments";
import { unitGroupLabel } from "../utils/departments";
import { statusCategory, useWorkflowConfig } from "../hooks/useWorkflow";
import { slaState } from "../utils/sla";
import type { SuggestionListFilters, SuggestionStatus } from "../types/ess";

export const DRAWER_WIDTH = 240;

// The rail's chrome is the package's now: `SideNavigation` reads
// `background.dark` for the paper, `divider` for the hairline, and
// `navItemBg` + `navActive` (falling back to `primary.main`) for the active
// row — the same tokens the hand-built rail used to read itself. What stays
// here is the ESS-specific content of the three slots: the Indorama logo, the
// product wordmark, the account card and the "by raven" footer.

export default function AppLayout({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const brand = theme.palette.primary.main;
  // `*_dark.webp` is the dark-ink artwork (for a light rail); `*_light.webp`
  // is the light-ink one. Both marks are flat ink, so neither survives on the
  // opposite surface — the wordmarks simply vanish.
  const logoVariant = theme.palette.mode === "dark" ? "light" : "dark";
  const { actions: topBarActions } = useTopBarActions();
  const titleOverride = useTopBarTitle();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  // Hide the sidebar nav (wordmark + three rows) until the user has
  // been verified for ESS access by an admin. RequireAuth already pins
  // unverified users to /profile or /change-password; this just keeps
  // the sidebar middle section empty on those pending screens so the
  // app doesn't tease destinations the user can't actually reach.
  const { data: currentProfile } = useCurrentUserProfile();
  const essVerified = currentProfile?.user_info?.ess_verified === true;

  // Drives the sla_breached facet predicate below — same wiring
  // Landing.tsx uses for `slaState`'s categoryOf (terminal vs open).
  const { data: workflow } = useWorkflowConfig();

  // Task 15 (ESS Procedure-Alignment): Departmental Heads and above are
  // not eligible to originate a suggestion (IEPL-CORP-2.207 §2.0). The
  // backend computes this per-user as `can_submit` on the same profile
  // fetch used for `essVerified` above. Unlike that gate (hidden until
  // proven true), this one is shown-by-default and hidden only on an
  // explicit `false` — absent/undefined (old backend, or not yet
  // computed) tolerates as eligible. Server remains authoritative (403
  // on create); this only removes the affordance from the UI.
  const canSubmit = !(
    currentProfile?.user_info && currentProfile?.can_submit === false
  );

  // An employee-only user sees just their own and on-behalf suggestions, so
  // the scheme-wide Overview row is dropped (RequireOverviewAccess guards
  // the route itself).
  const showOverview = !isEmployeeOnly(currentProfile?.roles);

  // Admin screens (categories, custodians, historical import) are
  // role-gated — the sidebar row mirrors the RequireAdmin route guard so
  // a non-admin is never shown a destination that would bounce them.

  // Sidebar count chip on the Suggestions tab. Reuses the same query key
  // shape as Reports' stats query — when it's already populated there,
  // this is a free cache hit. Wrapped with a long staleTime so opening
  // every page doesn't refetch.
  const sidebarStatsQuery = useQuery({
    queryKey: ["ess-stats", undefined, undefined, undefined],
    queryFn: () => getSuggestionStats({}),
    staleTime: 60_000,
  });
  const suggestionsTotal = sidebarStatsQuery.data?.total;

  // Mirror Landing.tsx's filter parsing so the query key matches exactly —
  // when the user has drilled in from an Overview tile, this hits the cache
  // populated by Landing rather than fetching twice.
  const onListPage =
    location.pathname === "/suggestions" || location.pathname === "/suggestions/";
  const { listFilters, listMine, hasActiveFilter } = ((): {
    listFilters: SuggestionListFilters;
    listMine: boolean;
    hasActiveFilter: boolean;
  } => {
    if (!onListPage)
      return { listFilters: {}, listMine: false, hasActiveFilter: false };
    const sp = new URLSearchParams(location.search);
    const splitCsv = (key: string) =>
      sp.get(key)?.split(",").filter(Boolean) ?? undefined;
    const filters: SuggestionListFilters = {
      status: splitCsv("status") as SuggestionStatus[] | undefined,
      category: splitCsv("category"),
      department: splitCsv("department"),
      unit: splitCsv("unit"),
      reward_category: splitCsv("reward_category"),
      date_from: sp.get("date_from") ?? undefined,
      date_to: sp.get("date_to") ?? undefined,
    };
    const mine = sp.get("mine") === "1";
    const active = !!(
      filters.status ||
      filters.category ||
      filters.department ||
      filters.unit ||
      filters.reward_category ||
      filters.date_from ||
      filters.date_to ||
      mine
    );
    return { listFilters: filters, listMine: mine, hasActiveFilter: active };
  })();

  // Same department -> unit-group lookup Landing builds — the Unit filter
  // is resolved client-side inside listSuggestions, and the query key must
  // mirror Landing's exactly for the cache-sharing noted above.
  const { data: departmentList } = useDepartments();
  const unitByDepartment = useMemo(() => {
    const m: Record<string, string> = {};
    (departmentList ?? []).forEach((d) => {
      if (d?.name) m[d.name] = unitGroupLabel(d);
    });
    return m;
  }, [departmentList]);

  const filteredListQuery = useQuery({
    queryKey: ["ess-list", listFilters, listMine, unitByDepartment],
    queryFn: () =>
      listSuggestions(
        {
          ...listFilters,
          my_suggestions: listMine || undefined,
        },
        unitByDepartment,
      ),
    enabled: onListPage && hasActiveFilter,
    staleTime: 60_000,
  });
  // The breadcrumb count must agree with what Landing actually renders
  // in the table. Backend list filters cover server-side facets
  // (status / dept / category / dates / mine); the `facet=aging` and
  // `facet=sla_breached` URL markers are enforced client-side because the
  // API has no updated_before / SLA predicate. Apply the same predicates
  // here so the header chip stays in sync with the list view's count —
  // `sla_breached` mirrors Landing.tsx's `allRows` predicate exactly
  // (`slaState(r, categoryOf).breached`).
  const facetParam = new URLSearchParams(location.search).get("facet");
  const filteredCount = (() => {
    const rows = filteredListQuery.data?.suggestions;
    if (!rows) return undefined;
    if (facetParam === "aging") {
      const cutoff = Date.now() - 14 * 86400000;
      return rows.filter(
        (r) => new Date(r.updated_at).getTime() < cutoff,
      ).length;
    }
    if (facetParam === "sla_breached") {
      const categoryOf = (status: string) => statusCategory(workflow, status);
      return rows.filter((r) => slaState(r, categoryOf).breached).length;
    }
    return rows.length;
  })();

  const isActive = (to: string) =>
    location.pathname === to ||
    (to !== "/suggestions" && location.pathname.startsWith(to));

  // When the user drilled into /suggestions from an Overview tile, the
  // ?from=overview marker tells AppLayout to keep "Overview" highlighted
  // in the sidebar (and "Suggestions" not). Filter-only visits to the
  // list (no marker) keep the regular Suggestions highlight.
  const cameFromOverview =
    onListPage &&
    new URLSearchParams(location.search).get("from") === "overview";

  const pageTitle = ((): ReactNode => {
    const p = location.pathname;
    if (p === "/suggestions" || p === "/suggestions/") {
      // Drilled in from an Overview tile? The tile passed its own label
      // through ?label=… so the header can echo what the user clicked
      // ("Passed SC", "Department · PP", "Aging") instead of just
      // "Suggestions". Without that marker, fall back to the generic
      // title plus a chip describing the filter state.
      const sp = new URLSearchParams(location.search);
      const sourceLabel = sp.get("label");
      if (cameFromOverview && sourceLabel) {
        // Breadcrumb format: "Overview / <drilldown label>". The Overview
        // crumb is a clickable link back to /overview; the drilldown label
        // stays bold for hierarchy.
        return (
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              component={Link}
              to="/overview"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                textDecoration: "none",
                "&:hover": { color: "text.primary" },
              }}
            >
              Overview
            </Box>
            <Box
              component="span"
              aria-hidden="true"
              sx={{ color: "text.secondary", fontWeight: 400, mx: -0.25 }}
            >
              /
            </Box>
            <span>{sourceLabel}</span>
            {filteredCount != null && <CountChip value={filteredCount} />}
          </Stack>
        );
      }
      // Count chip in the header title was removed — the page content
      // now carries an inline "Showing N results" / "APPLIED FILTERS ·
      // Showing N suggestions" summary above the table, which makes the
      // header-side chip redundant. Title stays plain "Suggestions"
      // regardless of filter state on the list page.
      return "Suggestions";
    }
    if (p.startsWith("/new-suggestion")) return "New Suggestion";
    if (p.startsWith("/overview")) return "Overview";
    if (p.startsWith("/profile")) return "My Profile";
    if (p.startsWith("/change-password")) return "Change Password";
    if (p.startsWith("/suggestions/admin/categories")) return "Categories";
    if (p.startsWith("/suggestions/admin/custodians"))
      return "Custodian Recipients";
    if (p.startsWith("/suggestions/admin/import")) return "Historical Import";
    if (p.startsWith("/suggestions/admin")) return "Admin";
    if (/^\/suggestions\/[^/]+$/.test(p)) return "Suggestion Detail";
    return "Suggestions";
  })();

  const backTarget = ((): string | null => {
    const p = location.pathname;
    // /suggestions is a top-level nav destination reached from the sidebar.
    // Filter params like ?mine=1 are not a drill-down and must NOT surface a
    // back arrow. Exception: explicit ?from=overview marker set by Overview's
    // tile drill-downs — then we do want the back affordance to /overview and
    // it persists across filter edits (Landing.tsx keeps the param sticky).
    if (p === "/suggestions" || p === "/suggestions/") {
      const sp = new URLSearchParams(location.search);
      return sp.get("from") === "overview" ? "/overview" : null;
    }
    if (p.startsWith("/new-suggestion")) return null;
    if (p.startsWith("/overview")) return null;
    if (p.startsWith("/profile")) return "/suggestions";
    if (p.startsWith("/change-password")) return "/suggestions";
    if (p.startsWith("/suggestions/admin/")) return "/suggestions/admin";
    return "/suggestions";
  })();

  const closeMobile = () => setMobileOpen(false);
  const closeDrawer = () => {
    setMobileOpen(false);
    setDesktopOpen(false);
  };

  // Nav rows as data for the package rail. The whole list is empty until
  // `essVerified`, the New Suggestion row is dropped on an explicit
  // `can_submit === false`, and Overview is dropped for employee-only users.
  const navItems: SideNavItem[] = !essVerified
    ? []
    : [
      ...(canSubmit
        ? [
          {
            label: "New Suggestion",
            to: "/new-suggestion",
            icon: <RavenIcon icon={CirclePlus} />,
            active: isActive("/new-suggestion"),
            dataTour: "nav-new-suggestion",
          } satisfies SideNavItem,
        ]
        : []),
      ...(showOverview
        ? [
          {
            label: "Overview",
            to: "/overview",
            icon: <RavenIcon icon={LayoutDashboard} />,
            active: isActive("/overview") || cameFromOverview,
            dataTour: "nav-overview",
          } satisfies SideNavItem,
        ]
        : []),
      {
        label: "Suggestions",
        to: "/suggestions",
        icon: <RavenIcon icon={ClipboardList} />,
        // Active on the list AND on the detail page (`/suggestions/:id`),
        // since the detail page is conceptually a child of the list.
        // Excludes `/suggestions/admin/*` (its own destination) and Overview
        // drill-downs (`?from=overview` keeps Overview highlighted instead).
        active:
          !cameFromOverview &&
          (onListPage ||
            (location.pathname.startsWith("/suggestions/") &&
              !location.pathname.startsWith("/suggestions/admin"))),
        trailing: <CountChip value={suggestionsTotal} />,
        dataTour: "nav-suggestions",
      },
    ];

  // Header slot: hamburger + Indorama colour logo, then the product wordmark
  // above the nav rows (`essVerified` only, same as the rows themselves).
  const railHeader = (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: 1, pt: 1, pb: 2 }}
      >
        <IconButton
          size="small"
          onClick={closeDrawer}
          aria-label="close navigation"
          sx={{ color: "text.secondary", p: 0 }}
        >
          <RavenIcon icon={Menu} fontSize="medium" />
        </IconButton>
        <Box
          component="img"
          src={`/images/indorama/logo_${logoVariant}.webp`}
          alt="Indorama"
          sx={{ display: "block", height: 36, width: "auto", userSelect: "none" }}
        />
      </Stack>
      {essVerified && (
        // Product wordmark just above the primary action. ~75% of the prior
        // 18px text-mark — a quiet brand anchor that doesn't compete with the
        // nav rows below.
        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            alignSelf: "flex-start",
            ml: 1.5,
            mb: 0.5,
            pr: "8px",
            fontFamily: "'Source Sans 3', 'Source Sans Pro', Inter, sans-serif",
            fontWeight: 700,
            fontSize: 13.5,
            letterSpacing: "-0.02em",
            color: "text.primary",
            lineHeight: 1,
            userSelect: "none",
            "&::after": {
              content: '""',
              position: "absolute",
              right: 0,
              bottom: "1px",
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: brand,
            },
          }}
        >
          Employee Suggestion Scheme
        </Box>
      )}
    </>
  );

  // Footer slot: the account card. Stays visible for unverified users so they
  // can still reach My Profile and Sign Out.
  const railFooter = (
    <Box sx={{ pt: 1.5, mt: 1.5 }}>
      <Box
        sx={{
          bgcolor: "action.hover",
          borderRadius: "12px",
          "& > *": { width: "100%" },
        }}
      >
        <SwitchUserMenu />
      </Box>
    </Box>
  );

  const railBranding = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={0.75}
      sx={{ pt: 1.25 }}
    >
      <Typography component="span" variant="caption" sx={{ color: "text.disabled" }}>
        by
      </Typography>
      <Box
        component="img"
        src={`/images/raven/raven_logo_${logoVariant}.webp`}
        alt="Raven"
        sx={{ height: 27, width: "auto", display: "block" }}
      />
    </Stack>
  );

  const rail = (variant: "persistent" | "temporary") => (
    <SideNavigation
      variant={variant}
      open={variant === "temporary" ? mobileOpen : desktopOpen}
      onClose={closeMobile}
      width={DRAWER_WIDTH}
      activePath={location.pathname}
      // Rows carry `to`, so react-router's Link owns the navigation and the
      // mobile sheet closes on the same click.
      onNavigate={closeMobile}
      linkComponent={Link}
      items={navItems}
      header={railHeader}
      footer={railFooter}
      branding={railBranding}
      // The AppShell sidebar region is already the `navigation` landmark.
      landmark={false}
      sx={
        variant === "temporary"
          ? { display: { xs: "block", md: "none" } }
          : {
            display: { xs: "none", md: "block" },
            // The package makes the docked *paper* sticky, but the paper's
            // parent is the Drawer root, which is only as tall as the paper
            // — so the rail scrolled away with the page and the account
            // card ended up mid-screen. The root is what has to stick: its
            // parent (the shell's sidebar region) spans the whole page.
            position: "sticky",
            top: 0,
            height: "100vh",
            "@supports (height: 100dvh)": { height: "100dvh" },
            "& .MuiDrawer-paper": {
              position: "relative",
              height: "100%",
              maxHeight: "100%",
            },
          }
      }
    />
  );

  const header = (
    <PageHeader
      className="no-print"
      title={titleOverride ?? pageTitle}
      onBack={backTarget ? () => navigate(backTarget) : undefined}
      backLabel="back"
      leading={
        <IconButton
          onClick={() => {
            setMobileOpen((o) => !o);
            setDesktopOpen((o) => !o);
          }}
          sx={{
            mr: 1,
            display: {
              xs: backTarget ? "none" : "inline-flex",
              md: desktopOpen ? "none" : "inline-flex",
            },
          }}
          aria-label="open navigation"
        >
          <RavenIcon icon={Menu} fontSize="medium" />
        </IconButton>
      }
      actions={
        <>
          {topBarActions}
          {/* New Suggestion CTA only on the Suggestions list page itself
            (`/suggestions`, plus the legacy `/suggestions/` trailing-slash).
            Hidden on:
            - Detail (`/suggestions/:id`), admin sub-routes — single-record
              or admin contexts.
            - Overview / Profile / etc. — wrong spots for "create new".
            - Overview drill-downs (`?from=overview` — Aging, Rejected,
              Passed WC, Submitted, etc.) — focused filtered views where
              the user is investigating, not authoring.
            - Ineligible submitter (`!canSubmit`, Task 15) — HOD/HOF and
              above don't get the affordance either, same as the sidebar
              nav row and mobile FAB below. */}
          {canSubmit &&
            (location.pathname === "/suggestions" ||
              location.pathname === "/suggestions/") &&
            new URLSearchParams(location.search).get("from") !== "overview" && (
            <>
              <Button
                variant="contained"
                startIcon={<RavenIcon icon={CirclePlus} />}
                onClick={() => navigate("/new-suggestion")}
                data-tour="new-suggestion-cta"
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                New suggestion
              </Button>
              <IconButton
                onClick={() => navigate("/new-suggestion")}
                aria-label="new suggestion"
                data-tour="new-suggestion-cta"
                sx={{
                  display: { xs: "inline-flex", sm: "none" },
                  width: 40,
                  height: 40,
                  // Mirror CTA_CONTAINED_BASE so the icon-only mobile
                  // variant matches the desktop "New suggestion" CTA:
                  // gradient, inset highlights, copper drop shadow,
                  // hover brighten, active press, focus ring. In dark mode
                  // the CTA tier is the lifted `primary.main` with a
                  // near-black label, exactly as MuiButton resolves it.
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : theme.palette.primary.dark,
                  backgroundImage:
                    "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 40%, rgba(0,0,0,0.08) 100%)",
                  color: theme.palette.primary.contrastText,
                  border: "1px solid rgba(255,255,255,0.18)",
                  boxShadow:
                    "inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.12), 0 2px 6px rgba(80,34,0,0.25)",
                  position: "relative",
                  isolation: "isolate",
                  overflow: "hidden",
                  transition:
                    "transform .1s ease, box-shadow .2s ease, filter .15s ease, background-color .15s ease",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(120deg, transparent 20%, rgba(255,255,255,0.35) 50%, transparent 80%)",
                    transform: "translateX(-120%)",
                    transition: "transform .7s ease",
                    pointerEvents: "none",
                    zIndex: 0,
                  },
                  "& > *": { position: "relative", zIndex: 1 },
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.main
                        : theme.palette.primary.dark,
                    filter: "brightness(1.04)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.40), inset 0 -1px 0 rgba(0,0,0,0.14), 0 3px 8px rgba(80,34,0,0.32)",
                  },
                  "&:hover::before": { transform: "translateX(120%)" },
                  "&:active": {
                    transform: "translateY(1px)",
                    backgroundColor:
                      theme.palette.navActive ?? theme.palette.primary.dark,
                    filter: "brightness(0.98)",
                    boxShadow:
                      `inset 0 1px 0 rgba(255,255,255,0.20), inset 0 -1px 0 rgba(0,0,0,0.18), 0 1px 2px rgba(80,34,0,0.32), 0 4px 10px -4px ${alpha(
                        theme.palette.navActive ?? theme.palette.primary.dark,
                        0.45,
                      )}`,
                  },
                  "&:focus-visible": {
                    outline: "none",
                    boxShadow:
                      `inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.12), 0 0 0 4px ${alpha(
                        theme.palette.primary.main,
                        0.3,
                      )}, 0 2px 6px rgba(80,34,0,0.25)`,
                  },
                }}
              >
                <RavenIcon icon={CirclePlus} fontSize="medium" />
              </IconButton>
            </>
          )}
        </>
      }
    />
  );

  return (
    <AppShell
      sidebar={
        <>
          {rail("temporary")}
          {rail("persistent")}
        </>
      }
      header={header}
      sidebarOpen={desktopOpen}
      sidebarWidth={DRAWER_WIDTH}
      // ESS pages carry their own cards, so the content region sits directly
      // on the page background rather than inside the shell's paper card.
      flush
    >
      <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0, maxWidth: "100%" }}>
        {children}
      </Box>
    </AppShell>
  );
}
